import React, { type ReactNode } from 'react';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import LinearGradient from 'react-native-linear-gradient';

import { Scroll } from '@/components/layout';
import { createStyles, useIsWindowed } from '@/hooks';

/** Light diagonal gradient shared by the unauthenticated screens (matches Figma background). */
const BACKGROUND_GRADIENT = ['#EEF3FF', '#F6F8FC', '#EEF3FF'];
const BACKGROUND_LOCATIONS = [0.08, 0.47, 0.91];

type AuthScreenBackgroundProps = {
  children: ReactNode;
  /**
   * Wrap the content in a keyboard-aware scroll view. Enable on screens with inputs
   * (Login, Forgot Password); leave off for static screens (Welcome). Default `false`.
   */
  scrollable?: boolean;
};

/**
 * Full-screen diagonal gradient background used across the auth flow. Centers its
 * children horizontally and optionally makes them keyboard-aware + scrollable.
 */
export function AuthScreenBackground({ children, scrollable = false }: AuthScreenBackgroundProps) {
  const styles = useStyles();
  // In iPad windowed mode the keyboard frame is measured against the full screen,
  // so `padding` behavior over-pads the bottom and hides the content. The keyboard
  // floats over a non-full-screen window there, so we skip avoidance entirely.
  const isWindowed = useIsWindowed();

  const gradient = (
    <LinearGradient
      colors={BACKGROUND_GRADIENT}
      locations={BACKGROUND_LOCATIONS}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.background}>
      {children}
    </LinearGradient>
  );

  if (!scrollable) {
    return gradient;
  }

  return (
    <KeyboardAvoidingView
      behavior={isWindowed ? undefined : 'padding'}
      style={styles.keyboardAvoidingView}>
      <Scroll bounces={false} flexGrow={1} showsVerticalScrollIndicator={false}>
        {gradient}
      </Scroll>
    </KeyboardAvoidingView>
  );
}

const useStyles = createStyles(() => ({
  keyboardAvoidingView: {
    flex: 1,
    width: '100%',
  },
  background: {
    flex: 1,
    alignItems: 'center',
  },
}));

export default AuthScreenBackground;
